import React from 'react';
import { useNavigate } from 'react-router-dom';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import CaseStudyProof, { caseStudyProofs } from '../../../Blog/CaseStudyProof';
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Users,
  Briefcase,
  UserSearch,
  Layers,
  Code2,
  ShieldCheck,
  Search,
  FileCheck,
  Clock,
  Handshake,
  MapPin,
  Mail,
  Globe,
  Landmark,
  ShoppingBag,
  Factory,
  HeartPulse,
  Gamepad2,
  Rocket,
  Truck,
  Quote,
  SlidersHorizontal,
  Zap,
  Crown,
} from 'lucide-react';

/**
 * Recruitment & Staffing Services — full showcase page.
 *
 * Positions SearchJob.ai as a full-service, AI-powered recruitment and
 * staffing partner. Modelled on how leading staffing firms (ALP
 * Consulting, Randstad, TeamLease) present: an employer-focused hiring
 * pitch, prominent impact stats, clearly-listed service lines,
 * industries served, a transparent process, testimonials and an
 * FAQ — all rendered in the SearchJob.ai landing theme (cream base,
 * forest-green primary, gradient accents, rounded-3xl cards, dark-ink
 * bands). Helpers are defined locally (mirroring AboutUs.jsx).
 *
 * Route: /hirewithus/staffing
 */

const INK = 'hsl(164 33% 7%)';

export default function StaffingServices() {
  const navigate = useNavigate();

  useSEO({
    title:
      'Recruitment & Staffing Services | SearchJob.ai — AI-Powered Hiring Partner',
    description:
      'SearchJob.ai is an AI-powered recruitment & staffing company — permanent recruitment, contract & temp staffing, RPO, executive search and IT staffing across India, with payroll, compliance and background verification managed for you. Hire faster, verified, and fully compliant.',
    canonical: 'https://searchjob.ai/hirewithus/staffing',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  const clients = [
    { name: 'Kinzy Club', logo: '/kinzy.png' },
    { name: 'Grand Gaming', logo: '/grandgaming.png' },
    { name: 'Idyaite', logo: '/idyaite.png' },
    { name: 'Creowis', logo: '/creowis.webp' },
    { name: 'key.ai', logo: '/keyai.png', dark: true },
    { name: 'manest.ai', logo: '/manest1.png' },
  ];

  // Each service card carries its own colour (gradient icon badge + tags)
  // so the grid is vibrant. Full class strings so Tailwind detects them.
  const services = [
    {
      icon: Users,
      title: 'Permanent Recruitment',
      desc: 'Full-time hires across every level — from individual contributors to leadership — sourced by AI and vetted by our recruiters before they ever reach your inbox.',
      tags: ['All levels', 'Pan-India'],
      iconGrad: 'from-emerald-500 to-teal-600',
      shadow: 'shadow-emerald-500/30',
      tag: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-700',
    },
    {
      icon: Briefcase,
      title: 'Contract & Temp Staffing',
      desc: 'Flexible contract, temp-to-hire and project talent, with onboarding, payroll and statutory compliance fully managed on your behalf.',
      tags: ['Flexi staffing', 'Payroll'],
      iconGrad: 'from-blue-500 to-indigo-600',
      shadow: 'shadow-blue-500/30',
      tag: 'bg-blue-500/10 border-blue-500/20 text-blue-700',
    },
    {
      icon: UserSearch,
      title: 'Executive Search',
      desc: 'Confidential leadership and niche senior hiring — headhunted from a curated network and assessed against your exact mandate.',
      tags: ['Leadership', 'Confidential'],
      iconGrad: 'from-violet-500 to-purple-600',
      shadow: 'shadow-violet-500/30',
      tag: 'bg-violet-500/10 border-violet-500/20 text-violet-700',
    },
    {
      icon: Layers,
      title: 'AI-native RPO',
      desc: 'Embed our recruiters and AI platform as your talent team. Scale hiring up or down on demand, without adding permanent headcount.',
      tags: ['Embedded', 'On-demand'],
      iconGrad: 'from-amber-500 to-orange-600',
      shadow: 'shadow-amber-500/30',
      tag: 'bg-amber-500/10 border-amber-500/20 text-amber-700',
    },
    {
      icon: Code2,
      title: 'IT & Tech Staffing',
      desc: 'Engineering, data, product and design talent — pre-screened with AI coding tests, proctoring and cheating detection, so skills are proven, not claimed.',
      tags: ['Engineers', 'Verified skills'],
      iconGrad: 'from-cyan-500 to-sky-600',
      shadow: 'shadow-cyan-500/30',
      tag: 'bg-cyan-500/10 border-cyan-500/20 text-cyan-700',
    },
    {
      icon: Crown,
      title: 'Fractional Leadership Hiring',
      desc: 'Part-time CFOs, CMOs, CHROs and functional heads for a few days a week — assessed on our platform before you meet them.',
      tags: ['CXOs & VPs', 'Days per week'],
      iconGrad: 'from-teal-500 to-emerald-600',
      shadow: 'shadow-teal-500/30',
      tag: 'bg-teal-500/10 border-teal-500/20 text-teal-700',
      to: '/hirewithus/fractional',
    },
    {
      icon: ShieldCheck,
      title: 'Payroll, Compliance & BGV',
      desc: 'Onboarding, payroll, statutory compliance and background verification for your contract workforce — managed by us, start to finish.',
      tags: ['Compliance', 'BGV'],
      iconGrad: 'from-rose-500 to-pink-600',
      shadow: 'shadow-rose-500/30',
      tag: 'bg-rose-500/10 border-rose-500/20 text-rose-700',
    },
  ];

  const featured = [
    {
      eyebrow: 'Contract & flexi staffing',
      title: 'A flexible workforce,',
      accent: 'without the paperwork.',
      body: 'Ramp a team up for a project, cover a seasonal peak, or trial talent before a permanent offer — we deploy pre-screened contract staff fast, and carry the employer-of-record burden for you.',
      points: [
        'Onboarding, payroll and payslips handled end to end',
        'Statutory compliance — PF, ESI, PT and more',
        'Background verification before day one',
        'Convert your best contractors to permanent roles when you are ready',
      ],
      img: '/staffing-contract-cut.png',
      alt: 'AI assistant managing contract staffing payroll and compliance',
      flip: false,
    },
    {
      eyebrow: 'AI-native RPO & embedded recruiting',
      title: 'Your talent team,',
      accent: 'on demand.',
      body: 'When hiring spikes, plugging in more agencies rarely helps. Our RPO model embeds dedicated recruiters plus the SearchJob.ai platform into your process — so you scale sourcing, screening and scheduling without scaling headcount.',
      points: [
        'Dedicated recruiters who learn your culture and how you hire',
        'Our AI does the sourcing, screening and interview scheduling',
        'One consistent pipeline, dashboards and weekly reporting',
        'Flex the team up or down as your roadmap changes',
      ],
      img: '/staffing-rpo-cut.png',
      alt: 'AI assistant powering an embedded RPO recruiting pipeline',
      flip: true,
    },
  ];

  // AI-native RPO — the common engagement models.
  const rpoFlavors = [
    {
      icon: Layers,
      title: 'End-to-end RPO',
      desc: 'We run your entire talent-acquisition function — from sourcing all the way to offer.',
      grad: 'from-emerald-500 to-teal-600',
    },
    {
      icon: Rocket,
      title: 'Project RPO',
      desc: 'A dedicated team for a specific hiring surge — for example, hire 50 engineers in 6 months.',
      grad: 'from-blue-500 to-indigo-600',
    },
    {
      icon: SlidersHorizontal,
      title: 'Selective / Modular RPO',
      desc: 'We handle specific stages only — just sourcing, or just screening — plugged into your process.',
      grad: 'from-violet-500 to-purple-600',
    },
    {
      icon: Zap,
      title: 'On-demand RPO',
      desc: 'Recruiters supplied on a flexible, per-need basis — scale the team up or down whenever you need.',
      grad: 'from-amber-500 to-orange-600',
    },
  ];

  // Each industry gets its own accent so the grid is colourful but still
  // on-brand. Full class strings (not interpolated) so Tailwind picks them up.
  const industries = [
    { icon: Code2, name: 'IT & Software', chip: 'bg-blue-500/10 border-blue-500/25 text-blue-600', hover: 'hover:border-blue-400/50' },
    { icon: Landmark, name: 'BFSI', chip: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-600', hover: 'hover:border-emerald-400/50' },
    { icon: ShoppingBag, name: 'E-commerce & Retail', chip: 'bg-amber-500/10 border-amber-500/25 text-amber-600', hover: 'hover:border-amber-400/50' },
    { icon: Factory, name: 'Manufacturing', chip: 'bg-indigo-500/10 border-indigo-500/25 text-indigo-600', hover: 'hover:border-indigo-400/50' },
    { icon: HeartPulse, name: 'Healthcare & Pharma', chip: 'bg-rose-500/10 border-rose-500/25 text-rose-600', hover: 'hover:border-rose-400/50' },
    { icon: Gamepad2, name: 'Gaming & Media', chip: 'bg-violet-500/10 border-violet-500/25 text-violet-600', hover: 'hover:border-violet-400/50' },
    { icon: Rocket, name: 'Startups & SaaS', chip: 'bg-fuchsia-500/10 border-fuchsia-500/25 text-fuchsia-600', hover: 'hover:border-fuchsia-400/50' },
    { icon: Truck, name: 'Logistics & Supply Chain', chip: 'bg-cyan-500/10 border-cyan-500/25 text-cyan-600', hover: 'hover:border-cyan-400/50' },
  ];

  // Colour flows through the funnel, one accent per step.
  const steps = [
    {
      n: '01',
      icon: Search,
      title: 'Share the role',
      desc: 'Tell us the role, must-have skills, budget and context — over a quick call or a short brief.',
      num: 'from-blue-500/40 to-blue-500/5',
      chip: 'bg-blue-500/10 border-blue-500/25 text-blue-600',
    },
    {
      n: '02',
      icon: Sparkles,
      title: 'AI-sourced shortlist',
      desc: 'Our platform surfaces best-fit candidates from our network and fresh sourcing, ranked by fit.',
      num: 'from-violet-500/40 to-violet-500/5',
      chip: 'bg-violet-500/10 border-violet-500/25 text-violet-600',
    },
    {
      n: '03',
      icon: FileCheck,
      title: 'Screen & verify',
      desc: 'AI screening, video interviews, coding and behavioural tests with cheating detection — reviewed by recruiters.',
      num: 'from-amber-500/40 to-amber-500/5',
      chip: 'bg-amber-500/10 border-amber-500/25 text-amber-600',
    },
    {
      n: '04',
      icon: Handshake,
      title: 'Interview & select',
      desc: 'You meet a curated, evidence-backed shortlist with full assessment reports, and pick your hire.',
      num: 'from-cyan-500/40 to-cyan-500/5',
      chip: 'bg-cyan-500/10 border-cyan-500/25 text-cyan-600',
    },
    {
      n: '05',
      icon: CheckCircle2,
      title: 'Onboard & support',
      desc: 'We handle the offer, onboarding and payroll/compliance for contract roles — then follow up after joining.',
      num: 'from-emerald-500/40 to-emerald-500/5',
      chip: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-600',
    },
  ];

  const edge = [
    {
      icon: Sparkles,
      title: 'AI + human, together',
      desc: 'The speed of the platform, with a recruiter making the judgment calls on every role.',
      iconGrad: 'from-violet-500 to-purple-600',
      shadow: 'shadow-violet-500/30',
    },
    {
      icon: Clock,
      title: 'Shortlists in ~72 hours',
      desc: 'AI sourcing compresses the slowest part of hiring, so you see candidates fast.',
      iconGrad: 'from-amber-500 to-orange-600',
      shadow: 'shadow-amber-500/30',
    },
    {
      icon: FileCheck,
      title: 'Verified, not just sourced',
      desc: 'Every candidate is screened, skill-assessed and background-checked before you meet them.',
      iconGrad: 'from-emerald-500 to-teal-600',
      shadow: 'shadow-emerald-500/30',
    },
    {
      icon: ShieldCheck,
      title: 'Compliance handled',
      desc: 'We run payroll, statutory compliance and background checks for your contract staff, so you do not have to.',
      iconGrad: 'from-blue-500 to-indigo-600',
      shadow: 'shadow-blue-500/30',
    },
  ];

  const testimonials = [
    {
      quote:
        'The SearchJob team is very competent and well staffed to handle any surge in hiring needs. They have unique technology to match the right candidates with the job requirements, and provide white-gloved treatment — constantly communicating with the candidate until they join and settle in.',
      who: 'Founder & CEO',
      org: 'Grand Gaming',
    },
    {
      quote:
        'Working with SearchJob.ai has been a game-changer for us at Kinzy Club. From understanding our unique culture to sourcing candidates with the right skills and the right attitude, their attention to detail and commitment were unmatched.',
      who: 'Co-Founder',
      org: 'Kinzy Club',
    },
    {
      quote:
        'As an early-stage AI team, every hire is critical and we have no time to sift through hundreds of resumes. The SearchJob.ai recruiters plugged in like part of our team — the AI-screened shortlist was sharp, and we closed senior engineers in weeks, not months.',
      who: 'Founder & CEO',
      org: 'manest.ai',
    },
  ];

  const faqs = [
    {
      q: 'How is this different from a traditional recruitment agency?',
      a: 'We run our own AI platform — sourcing, resume screening, AI video interviews, coding tests and cheating detection — behind a team of recruiters. You get the reach and speed of technology with the judgment and accountability of a dedicated partner.',
    },
    {
      q: 'How fast can you deliver candidates?',
      a: 'For most roles we deliver a first shortlist within roughly 72 hours. Because our AI does the sourcing and first-round screening, you spend your time only on interview-ready, verified candidates.',
    },
    {
      q: 'Do you handle contract staff payroll and compliance?',
      a: 'Yes. For contract and temporary staffing we act as the employer of record — onboarding, payroll, payslips, statutory compliance (PF, ESI, PT) and background verification are all managed for you.',
    },
    {
      q: 'What roles and seniority levels do you cover?',
      a: 'Everything from high-volume and entry-level roles to specialist engineers and confidential leadership searches — across IT, BFSI, retail, manufacturing, healthcare, gaming, startups and logistics.',
    },
    {
      q: 'How do you price your services?',
      a: 'Permanent recruitment is typically success-based (you pay on a joined hire). Contract staffing and RPO are priced per resource or as a monthly engagement. Tell us the role and we will share a clear, written proposal.',
    },
    {
      q: 'Can we use just the platform instead of the full service?',
      a: 'Absolutely. SearchJob.ai is a product first — you can run your own hiring on the platform. The staffing service is for teams who would rather we run it for them.',
    },
  ];

  const stats = [
    { v: '~72h', l: 'To first shortlist' },
    { v: '15+', l: 'Industries served' },
    { v: '10k+', l: 'Candidates in our network' },
    { v: '1:1', l: 'Dedicated account manager' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO (split) ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-16">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] xl:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.06] mb-6">
                  Your partner for talent,
                  <br />
                  <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                    powered by AI.
                  </span>
                </h1>
                <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
                  SearchJob.ai isn&apos;t just software. Our recruiters run
                  your hiring end to end — permanent, contract, RPO and
                  executive search — using our own AI platform to move
                  faster and hand you candidates who are already screened
                  and verified.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <Button
                    size="lg"
                    onClick={() => navigate('/hirewithus/contact')}
                    className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
                  >
                    Hire Talent
                    <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate('/hirewithus')}
                    className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
                  >
                    Explore the AI platform
                  </Button>
                </div>
                <div className="mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-muted-foreground">
                  <TrustDot text="Permanent & contract" />
                  <TrustDot text="RPO & executive search" />
                </div>
              </div>

              {/* Hero image */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl rounded-[3rem] -z-10" />
                <img
                  src="/staffing-hero-cut.png"
                  alt="SearchJob.ai AI assistant welcoming newly hired candidates"
                  className="w-full h-auto drop-shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ═════════════ TRUST BAR — running logo marquee ═════════════ */}
        <section className="relative py-10 lg:py-12 bg-muted/30 border-y border-border/60 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-8">
              Companies that hire with SearchJob.ai
            </p>
          </div>

          {/* Full-bleed marquee rail, edge-faded, pauses on hover */}
          <div
            className="relative group/logos"
            style={{
              maskImage:
                'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
              WebkitMaskImage:
                'linear-gradient(to right, transparent, black 12%, black 88%, transparent)',
            }}
          >
            <div className="flex w-max staffing-logo-track">
              {/* Two identical copies; the -50% shift lands on the start of
                  the second, so the loop is seamless. Each copy repeats the
                  clients so the rail stays full on wide screens. */}
              {[0, 1].map((copy) => (
                <div
                  key={copy}
                  aria-hidden={copy === 1}
                  className="flex items-center gap-10 sm:gap-14 pr-10 sm:pr-14"
                >
                  {[...clients, ...clients].map((c, idx) => (
                    <div
                      key={`${copy}-${idx}-${c.name}`}
                      className={`flex items-center justify-center shrink-0 ${
                        c.dark ? 'bg-zinc-900 rounded-xl px-4 py-2.5' : ''
                      }`}
                    >
                      <img
                        src={c.logo}
                        alt={c.name}
                        draggable="false"
                        className="h-9 sm:h-11 w-auto max-w-[160px] object-contain opacity-90 hover:opacity-100 hover:scale-105 transition-all duration-300 select-none"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <style>{`
            @keyframes staffing-logo-scroll {
              from { transform: translateX(0); }
              to   { transform: translateX(-50%); }
            }
            .staffing-logo-track {
              animation: staffing-logo-scroll 30s linear infinite;
            }
            .group\\/logos:hover .staffing-logo-track {
              animation-play-state: paused;
            }
            @media (prefers-reduced-motion: reduce) {
              .staffing-logo-track { animation: none; }
            }
          `}</style>
        </section>

        {/* ═════════════ WHO WE ARE ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Who we are</Eyebrow>
              <SectionHeading
                plain="A full-service recruitment firm,"
                accent="with our own AI built in."
              />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Most agencies source candidates by hand. We run the same AI
                platform that powers SearchJob.ai — sourcing, screening, AI
                interviews, coding tests and cheating detection — behind a
                team of experienced recruiters.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                So you get the speed and reach of software with the
                judgment and accountability of a real recruiting team. One
                team owns your role from the first brief to the day your
                hire joins — and stays in touch until they have settled in.
              </p>
              {/* <div className="rounded-3xl border border-primary/25 bg-primary/5 p-6">
                <p className="text-base text-foreground leading-relaxed">
                  <strong className="text-primary">
                    Product first, service on top.
                  </strong>{' '}
                  Use the platform yourself, or let our recruiters run it for
                  you. Either way, you hire faster — and only meet verified,
                  interview-ready candidates.
                </p>
              </div> */}
            </div>
            <ImageCard
              src="/staffing-team-cut.png"
              alt="AI assistant working alongside the SearchJob.ai recruiting team"
            />
          </div>
        </Section>

        {/* ═════════════ SERVICES GRID ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>What we offer</Eyebrow>
            <SectionHeading
              center
              plain="Staffing solutions for"
              accent="every kind of hire."
            />
            <p className="text-lg text-muted-foreground leading-relaxed">
              From a single critical hire to scaling an entire team — pick
              the engagement that fits, or combine several.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  onClick={s.to ? () => navigate(s.to) : undefined}
                  role={s.to ? 'link' : undefined}
                  className={`group bg-card border border-border/70 rounded-3xl p-6 lg:p-7 shadow-sm hover:shadow-[0_18px_60px_-18px_hsl(var(--primary)/0.28)] hover:-translate-y-1 transition-all duration-300 ${s.to ? 'cursor-pointer' : ''}`}
                >
                  <div
                    className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.iconGrad} text-white flex items-center justify-center shadow-lg ${s.shadow} mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 leading-snug">
                    {s.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {s.desc}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.tags.map((t) => (
                      <span
                        key={t}
                        className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border ${s.tag}`}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ FEATURED HIGHLIGHTS + RPO FLAVOURS ═════════════ */}
        <Section>
          <div className="space-y-16 lg:space-y-24">
            {featured.map((f) => (
              <FeatureRow key={f.eyebrow} {...f} />
            ))}
          </div>

          {/* ── AI-native RPO — the flavours ── */}
          <div className="mt-20 lg:mt-28">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <Eyebrow center>Common RPO flavours</Eyebrow>
              <SectionHeading
                center
                plain="Pick the RPO model"
                accent="that fits your team."
              />
              <p className="text-lg text-muted-foreground leading-relaxed">
                RPO isn&apos;t one-size-fits-all. Plug our recruiters and AI in
                at exactly the level you need — a full function, a single
                surge, or just the stages you want.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {rpoFlavors.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.title}
                    className="group bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_48px_-16px_hsl(var(--primary)/0.25)] transition-all duration-300"
                  >
                    <div
                      className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.grad} text-white flex items-center justify-center shadow-lg mb-4 group-hover:scale-110 transition-transform duration-300`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                      {f.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Section>

        {/* ═════════════ INDUSTRIES ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Industries we serve</Eyebrow>
            <SectionHeading
              center
              plain="Domain recruiters for"
              accent="the sectors you hire in."
            />
            <p className="text-lg text-muted-foreground leading-relaxed">
              The recruiter on your role already speaks your language, knows
              the market rate, and knows where the best people are.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {industries.map((ind) => {
              const Icon = ind.icon;
              return (
                <div
                  key={ind.name}
                  className={`group flex flex-col items-center text-center gap-3 rounded-3xl border border-border/70 bg-card px-4 py-6 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 ${ind.hover}`}
                >
                  <span
                    className={`w-12 h-12 rounded-2xl border flex items-center justify-center group-hover:scale-110 transition-transform duration-300 ${ind.chip}`}
                  >
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="text-sm font-semibold text-foreground leading-snug">
                    {ind.name}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ HOW WE WORK ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>How we work</Eyebrow>
            <SectionHeading
              center
              plain="From brief to joined,"
              accent="in five clear steps."
            />
            <p className="text-lg text-muted-foreground leading-relaxed">
              A clear process, one team accountable from start to finish,
              and our AI handling the repetitive work at every stage.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {steps.map((st) => {
              const Icon = st.icon;
              return (
                <div
                  key={st.n}
                  className="relative bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-[0_16px_48px_-16px_hsl(var(--primary)/0.25)] transition-all duration-300"
                >
                  <span
                    className={`text-4xl font-black bg-gradient-to-br ${st.num} bg-clip-text text-transparent leading-none`}
                  >
                    {st.n}
                  </span>
                  <div
                    className={`w-10 h-10 rounded-xl border flex items-center justify-center my-3 ${st.chip}`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {st.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {st.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ WHY US ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>The SearchJob edge</Eyebrow>
            <SectionHeading
              center
              plain="Why teams staff with us"
              accent="instead of a traditional agency."
            />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {edge.map((e) => {
              const Icon = e.icon;
              return (
                <div
                  key={e.title}
                  className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_16px_48px_-16px_hsl(var(--primary)/0.25)] transition-all duration-300"
                >
                  <div
                    className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${e.iconGrad} text-white flex items-center justify-center shadow-lg ${e.shadow} mb-4`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                    {e.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {e.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ IMPACT BAND (dark) ═════════════ */}
        <section
          className="relative py-14 lg:py-20 overflow-hidden text-white"
          style={{ backgroundColor: INK }}
        >
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/20 bg-white/10 backdrop-blur-sm text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                By the numbers
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
                Built to move faster than a{' '}
                <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                  traditional agency.
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {stats.map((s) => (
                <div key={s.l}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {s.v}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ TESTIMONIALS ═════════════ */}
        <Section muted>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Client stories</Eyebrow>
            <SectionHeading
              center
              plain="Teams that trusted us"
              accent="with their hiring."
            />
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map((t) => (
              <div
                key={t.org}
                className="relative bg-card border border-border/70 rounded-3xl p-7 lg:p-8 shadow-sm"
              >
                <Quote
                  className="w-9 h-9 text-primary/15 mb-3"
                  fill="currentColor"
                />
                <p className="text-base text-foreground/85 leading-relaxed italic mb-5">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-emerald-800 text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0">
                    {t.org.charAt(0)}
                  </span>
                  <div>
                    <div className="text-sm font-bold text-foreground">
                      {t.who}
                    </div>
                    <div className="text-xs text-muted-foreground">{t.org}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 max-w-6xl mx-auto">
            <CaseStudyProof
              items={[caseStudyProofs.grandGaming, caseStudyProofs.kinzy]}
            />
          </div>
        </Section>

        {/* ═════════════ FAQ ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>FAQ</Eyebrow>
            <SectionHeading
              center
              plain="Questions teams ask"
              accent="before they hire with us."
            />
          </div>
          <div className="space-y-4 max-w-3xl mx-auto">
            {faqs.map((f) => (
              <FAQItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Section>

        {/* ═════════════ FINAL CTA (dark) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                    Looking to hire?
                    <br />
                    <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                      Let&apos;s find your next great hire.
                    </span>
                  </h2>
                  <p className="text-lg text-white/70 leading-relaxed mb-8">
                    Tell us the role — we&apos;ll come back with an
                    AI-sourced, recruiter-verified shortlist. Permanent,
                    contract, or an entire team.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      size="lg"
                      onClick={() => navigate('/hirewithus/contact')}
                      className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                    >
                      Request Talent
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={() => navigate('/hirewithus/demo')}
                      className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                    >
                      Book a Demo
                    </Button>
                  </div>
                </div>

                <div className="lg:pl-8 lg:border-l lg:border-white/10 space-y-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                    Talk to our staffing team
                  </p>
                  <DarkContactLine icon={MapPin} text="Bangalore, India" />
                  <DarkContactLine icon={Mail} text="contact@searchjob.ai" />
                  <DarkContactLine icon={Globe} text="www.searchjob.ai" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Local building blocks — mirror the landing / AboutUs page        */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false, id }) {
  return (
    <section
      id={id}
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[480px] mx-auto h-auto drop-shadow-xl"
      />
    </div>
  );
}

function FeatureRow({ eyebrow, title, accent, body, points, img, alt, flip }) {
  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div className={flip ? 'lg:order-2' : ''}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <SectionHeading plain={title} accent={accent} />
        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
          {body}
        </p>
        <CheckList items={points} />
      </div>
      <ImageCard src={img} alt={alt} className={flip ? 'lg:order-1' : ''} />
    </div>
  );
}

function TrustDot({ text }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CheckCircle2 className="w-4 h-4 text-primary" />
      {text}
    </span>
  );
}

function DarkContactLine({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-emerald-300" />
      </span>
      <span className="text-sm font-medium text-white/90">{text}</span>
    </div>
  );
}

function FAQItem({ q, a }) {
  return (
    <details className="group bg-card border border-border/70 rounded-2xl p-5 shadow-sm open:shadow-md transition-shadow">
      <summary className="flex items-center justify-between gap-3 cursor-pointer list-none">
        <span className="text-base font-bold text-foreground leading-snug">
          {q}
        </span>
        <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary transition-transform duration-300 group-open:rotate-45">
          <span className="text-lg leading-none font-light">+</span>
        </span>
      </summary>
      <p className="text-sm text-muted-foreground leading-relaxed mt-3">
        {a}
      </p>
    </details>
  );
}
